/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {Alert, Placement} from '@ion/dls/src/components/alert/Alert';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import Render from '@ion/dls/src/render/Render';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {setNotifyOnCreatedJob} from './signals/createJobNotifications';
import {getBannerSeen, setBannerSeen} from './JobNotificationsOptIn';
import {getWatchCreatedJobsDefault, setWatchCreatedJobsDefault} from './WatchedJobs';

const useStyles = makeStyles(theme => ({
  banner : {
    margin            : 0,
    '&[role="alert"]' : {
      backgroundColor : dlsColor('paleBlue'),
      borderRadius    : 4,
      margin          : '0px 0px 16px',
      padding         : '24px 28px'
    },
    '& > div:last-of-type' : {
      alignSelf : 'center'
    },
    [theme.breakpoints.down('sm')] : {
      '&[role="alert"]' : {
        padding : '18px 20px'
      }
    }
  },
  bannerButton : {
    '&.MuiButton-root' : {
      padding    : 0,
      whiteSpace : 'nowrap'
    }
  }
}));

/**
 * @CreateJobNotificationsBanner
 *
 * The one-time offer, at the top of the Create Job dialog's first step, to
 * watch created jobs by default.
 *
 * ADM owns this node and hands it to the shared dialog as a slot, because the
 * dialog lives in `@ion/applications` and cannot reach the watch preference.
 *
 * This is the only surface in the dialog which *writes* the stored default.
 * `CreateJobNotificationsToggle`, which sits at the bottom of the same step,
 * only reads it - the two stay in step through the `notifyOnCreatedJob`
 * signal rather than through shared component state, which they cannot have
 * now that they render in different places.
 *
 * The button's label names what pressing it will do, so it reads Disable for
 * a user whose stored default is already on. Either way the press answers the
 * banner and dismisses it for good - the per-job toggle at the bottom of the
 * step is where the answer stays visible and changeable from there.
 *
 * ```typescript
 *
 *   import CreateJobNotificationsBanner from '.../CreateJobNotificationsBanner';
 *
 *   <CreateJobEditor jobNotificationsBanner={<CreateJobNotificationsBanner />} />
 *
 * ```
 */
export const CreateJobNotificationsBanner = () => {
  const classes = useStyles();

  const [watchByDefault, setWatchByDefault] = useState<boolean>(getWatchCreatedJobsDefault);

  const [bannerSeen, setSeen] = useState<boolean>(true);

  useEffect(() => {
    let mounted = true;

    getBannerSeen().then(seen => {
      if (mounted) {
        setSeen(seen);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  /**
   * The banner's one button. Its label names the action, so pressing it stores
   * the opposite of what is stored now - and publishes the same value to the
   * signal, which is what moves the toggle at the bottom of the dialog.
   *
   * The banner goes with the press. `setSeen` hides it for the rest of this
   * open; `setBannerSeen` keeps it hidden on every open after.
   */
  const onToggleDefault = useCallback(() => {
    const next = !watchByDefault;

    setWatchByDefault(next);
    setNotifyOnCreatedJob(next);
    setSeen(true);
    setWatchCreatedJobsDefault(next);
    setBannerSeen();
  }, [watchByDefault]);

  return (
    <Render.If condition={!bannerSeen}>
      <Alert type='info'
        className={classes.banner}
        title={translate('jobsearchengine:createJob.banner.title')}
        message={
          <div>
            <div>
              {translate('jobsearchengine:createJob.banner.message')}
            </div>
            <div>
              {translate('jobsearchengine:createJob.banner.preferences')}
            </div>
          </div>
        }
        action={
          <TertiaryButton className={classes.bannerButton}
            fontSize='14px'
            onClick={onToggleDefault}
            noUnderline
            uppercase>
            {translate(watchByDefault
              ? 'jobsearchengine:createJob.banner.disable'
              : 'jobsearchengine:createJob.banner.enable')}
          </TertiaryButton>
        }
        placement={Placement.right}
        gutters={false} />
    </Render.If>
  );
};

export default React.memo(CreateJobNotificationsBanner);
